import { Card, CardContent, CardHeader, Container, Grid, Paper, Skeleton, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Create, DateInput, Edit, FormDataConsumer, required, SaveButton, SimpleForm, TextInput,  Toolbar, useEditController,  useNotify,  useRedirect,  useTranslate } from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { ItemsTable } from '../logic/ItemsTable'
import { useFormContext, useFormState } from 'react-hook-form';
import { billingServices } from 'src/services/reservation/billing/billing.service'
import { DatePickers } from 'src/components/uiElements/react-hook-fields'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { CardCommon } from 'src/components/uiElements'
import { currencyServices } from 'src/services/reservation/currency/currency.service'


const CustomToolbar = () => {

  const { isSubmitting } = useFormState();
  const { setValue } = useFormContext();

  setValue('__externalDirty', true, {
        shouldDirty: true,
        shouldTouch: false,
    });

  return (
    <Toolbar>
        <SaveButton
            disabled={isSubmitting}
        />
    </Toolbar>
)}

export const SupplierBillingEdit = (props,{id}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const { record } = useEditController({ resource: 'supplier-billing', id: props.id })
  const [loadingBasesList, setLoadingBasesList] = useState(false)
  const [basesList, setBasesList] = useState([])
  const [loadingCurrencyList, setLoadingCurrencyList] = useState(false)
  const [currencyList, setCurrencyList] = useState([])
  const [allItems, setAllItems] = useState()
  const [selectedItems, setSelectedItems] = useState([])
  const [itemsToSelect, setItemsToSelect] = useState( [])
  const [originallySelectedItems, setOriginallySelectedItems] = useState([])
  const [isCreditNote, setIsCreditNote] = useState(false)

  const documenTypeOptions = ['invoice', 'debit_note', 'credit_note']

  useEffect(() => {
    getBases()
    getCurrencies()
  }, [])

  useEffect(() => {
    if (record && allItems) {
      setIsCreditNote(record.document_type === 'credit_note' ? true : false)
      const initialitems = record?.supplier_billing_item_relation?.map(item => {
        const currency_relation = allItems.find(elem => elem.id === item.item_supplier_relation?.id)?.currency_relation
          ?? record?.currency_relation
        return {
          ...item.item_supplier_relation,
          supplier_name: record.supplier_name,
          supplier_payment_order_item_id: item.id,
          amount_pending: item.item_supplier_relation?.amount_pending,
          amount: item.amount,
          currency_relation
        }
      }) ?? []
      setSelectedItems(initialitems)
      setItemsToSelect(initialitems)
      setOriginallySelectedItems(initialitems)
    }
  }, [record, allItems])

  const getBases = async () =>{
    setLoadingBasesList(true)
    await billingServices.getBillingBases().then(res=>{
      const baseArray = Object.entries(res?.options ?? [])
      setBasesList(baseArray
        .map(base => ({ name: base[0], id: base[1] }))
        .filter((base, index, self) => self.findIndex(elem => elem.id === base.id) === index)
      )
    }).finally(()=>{
      setLoadingBasesList(false)
    })
  }

  const getCurrencies = async () => {
    try {
      setLoadingCurrencyList(true)
      const allCurrencies = await currencyServices
      .getByAll()
      setCurrencyList(allCurrencies)
      setLoadingCurrencyList(false)
    } catch (error) {
      setCurrencyList([])
      setLoadingCurrencyList(false)
    }
  }

  const onTransform = (data) => {
    return {
      ...data,
      supplier_billing_item_relation: itemsToSelect.map(item=>{
        return {
          id: item.supplier_payment_order_item_id,
          item_id: item.item_id,
          supplier_billing_id: record.id,
          amount: item.amount_pending
        }
      })
    }
  }

  const onSuccess = async () => {
    notify('ra.notification.updated_success_femenine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.supplier-billing') },
    })
    setSelectedItems([])
    setItemsToSelect([])
    redirect('/supplier-billing')
  }

  const onError = () => {
    notify('ra.notification.updated_error_femenine', {
      type: 'error',
      messageArgs: { element: translate('ra.single_entity.supplier-billing') },
    })
  }

  return (
    <Edit 
      mutationMode='pessimistic'
      mutationOptions={{ onSuccess, onError }}
      actions={null}
      transform={onTransform}
      {...props}
    >
      <CardCommon title={translate('ra.title.update_billing')}>
        <SimpleForm toolbar={<CustomToolbar/>}>
          <Grid width='100%' display='flex' flexDirection='column' rowGap={6}>
            <CardCommon title={translate('ra.title.suppliers')}>
              <Grid container spacing={3}>
                <EditInputsWrap className='small' label={translate('ra.field.supplier')} required md={6} lg={6}>
                  <TextField
                    fullWidth
                    size='small'
                    value={`${record?.supplier_relation?.id} - ${record?.supplier_relation?.name}`}
                    disabled
                  />
                </EditInputsWrap>

                <EditInputsWrap className='small' label={translate('ra.field.code')} md={3} lg={2}>
                  <TextField
                    fullWidth
                    size='small'
                    value={record?.supplier_relation?.code}
                    disabled
                  />
                </EditInputsWrap>
              </Grid>
            </CardCommon>

            <CardCommon title={translate('ra.title.voucher_details')}>
              <Grid container spacing={3}>
                <EditInputsWrap className='small' label={translate('ra.field.document_type')} required lg={3}>
                  <SelectInput
                    source='document_type'
                    fullWidth
                    label={false}
                    options={documenTypeOptions.map(option=>({
                      id: option,
                      name: translate(`ra.field.${option}`)
                    }))}
                    optionsText={['name']}
                  />
                </EditInputsWrap>

                <EditInputsWrap className='small' label={translate('ra.field.document_number')} required lg={3}>
                  <TextInput
                    source='document_number'
                    fullWidth
                    label=''
                    validate={[required()]}
                  />
                </EditInputsWrap>

                <EditInputsWrap className='small' label={translate('ra.field.billing_base')} lg={3}>
                  {loadingBasesList ? 
                    <Skeleton variant='rectangular' width={'100%'} height={40} />
                  :
                    <SelectInput
                      source='base'
                      fullWidth
                      options={basesList}
                      label=''
                      defaultValue={record?.base?.id}
                      optionsText={['name']}
                    />
                  }
                </EditInputsWrap>

                <EditInputsWrap className='small' label={translate('ra.field.currency')} lg={3}>
                  {loadingCurrencyList ? (
                    <Skeleton variant='rectangular' width={'100%'} height={40} />
                  ) : (
                    <FormDataConsumer>
                      {({ formData }) => (
                        <SelectInput
                          size='small'
                          fullWidth
                          label={false}
                          source='currency_id'
                          options={currencyList}
                          optionsText={['long_name']}
                          getOptionLabel={item => (
                            `${item.symbol} ${item.long_name} (${item.short_name})`
                          )}
                          validate={[required()]}
                          // defaultValue={formData.supplier?.currency_id}
                          disabled
                        />
                      )}
                    </FormDataConsumer>
                  )}
                </EditInputsWrap>

                <EditInputsWrap className='small' label={translate('ra.field.voucher_date')} required lg={4}>
                  <DatePickers
                    source='date'
                    viewsShow={false}
                    defaultHour={0}
                    defaultMinute={0}
                    style={{ width: '100%' }}
                    required
                  />
                </EditInputsWrap>

                <EditInputsWrap className='small' label={translate('ra.field.due_date')} required lg={4}>
                  <DatePickers
                    source='due_date'
                    viewsShow={false}
                    defaultHour={0}
                    defaultMinute={0}
                    style={{ width: '100%' }}
                    required
                  />
                </EditInputsWrap>

                <EditInputsWrap className='small' label={translate('ra.field.accounting_date')} required lg={4}>
                  <DatePickers
                    source='accounting_date'
                    viewsShow={false}
                    defaultHour={0}
                    defaultMinute={0}
                    style={{ width: '100%' }}
                    required
                  />
                </EditInputsWrap>
              </Grid>
            </CardCommon>

            <CardCommon title={translate('ra.title.item_selected')}>
              <ItemsTable
                hideTitle={true}
                supplierId={null}
                selectedItems={itemsToSelect}
                setSelectedItems={setItemsToSelect}
                editData={selectedItems}
                enableFilters={false}
                isCreditNote={isCreditNote}
                hiddenColumns={['amount_paid', 'amount_pending']}
              />
            </CardCommon>

            <CardCommon title={translate('ra.title.item_total')}>
              <ItemsTable
                hideTitle={true}
                supplierId={record?.supplier_id}
                selectedItems={itemsToSelect}
                setSelectedItems={setItemsToSelect}
                originallySelectedItems={originallySelectedItems}
                isCreditNote={isCreditNote}
                setAllItems={setAllItems}
                hiddenColumns={['amount_paid', 'amount_pending']}
              />
            </CardCommon>
          </Grid>
        </SimpleForm>
      </CardCommon>
    </Edit>
  )
}
